import { RefObject, useEffect } from 'react';

import { checkStringLength } from 'utils/string';
import { mapUsersWithAvatar } from 'utils/filterOptions';
import { useDebounce } from 'hooks/useDebounce';
import useLazyLoading from 'hooks/useLazyLoading';
import * as userService from 'services/user';
import { DEFAULT_PAGE } from 'constants/page';
import { MAKE_SEARCH_BY } from 'constants/fetchTypes';
import { DEFAULT_PAGE_SIZE, MIN_USER_SEARCH_LENGTH } from 'constants/appConstants';
import type { QueryObject } from 'interface/queryParam';

interface EmployeeDebounceWithLazyLoadProps {
  employeeId?: string | number;
  additionalFetchProps?: QueryObject;
  searchUserByFullName?: boolean;
}

interface EmployeeResponseMeta {
  total: number;
  page: number;
  size: number;
}

interface EmployeeResponse {
  data: OptionWithAvatar[];
  meta: EmployeeResponseMeta;
}

interface EmployeeResultItem {
  id: string | number;
  fullname?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  empId?: string;
}

type OptionWithAvatar = ReturnType<typeof mapUsersWithAvatar>[number];
type LastRefHandler = ((node: Element | null) => void) | RefObject<HTMLAnchorElement>;
type DebouncedSetter = (value: string) => void;

type UseEmployeeDebounceWithLazyLoadResult = [boolean, OptionWithAvatar[], string, DebouncedSetter, LastRefHandler];

const useEmployeeDebounceWithLazyLoad = ({
  employeeId,
  additionalFetchProps,
  searchUserByFullName = false,
}: EmployeeDebounceWithLazyLoadProps): UseEmployeeDebounceWithLazyLoadResult => {
  const [code, debounceFunction] = useDebounce();

  const getDataFunction = async (filters: QueryObject): Promise<EmployeeResponse> => {
    if (!employeeId && checkStringLength(code, MIN_USER_SEARCH_LENGTH)) {
      return { data: [], meta: { total: 0, page: DEFAULT_PAGE, size: DEFAULT_PAGE_SIZE } };
    }

    const params: QueryObject = {
      q: code,
      ...filters,
      ...additionalFetchProps,
    };

    if (!searchUserByFullName) {
      params.searchBy = MAKE_SEARCH_BY.join(',');
    }

    const response = await userService.fetchAll(params);
    const safeData = Array.isArray(response.data) ? (response.data as EmployeeResultItem[]) : [];
    const safeMeta =
      typeof response.meta === 'object' && response.meta
        ? (response.meta as EmployeeResponseMeta)
        : { total: 0, page: DEFAULT_PAGE, size: DEFAULT_PAGE_SIZE };

    return { data: mapUsersWithAvatar(safeData), meta: safeMeta };
  };

  const { loading, dataList, lastRef, reset } = useLazyLoading<OptionWithAvatar>(getDataFunction);

  useEffect(() => {
    reset();
  }, [code, employeeId, reset]);

  return [loading, dataList, code, debounceFunction, lastRef];
};

export default useEmployeeDebounceWithLazyLoad;
